/**
 * Responsibility: Shape commerce plan data into field-assist metadata and values for admin editing.
 * Scope: Commerce plan form metadata/value normalization only; persistence stays elsewhere.
 */
import type { FormMetaData } from 'field-assist'
import {
  COMMERCE_PLAN_IDS,
  type CommercePlan,
  type CommercePlanId,
  normalizeCommercePlan,
} from '../../commerce/commerce-contract'

const PLAN_OPTIONS: Array<{ text: string; value: CommercePlanId }> = COMMERCE_PLAN_IDS.map((id) => ({
  text: id[0].toUpperCase() + id.slice(1),
  value: id,
}))

export function buildAdminCommerceInitialValues(plan?: Partial<CommercePlan>): Record<string, string | number | boolean> {
  const normalized = normalizeCommercePlan(plan, plan?.id)
  return {
    id: normalized.id,
    label: normalized.label,
    monthlyPriceUsdCents: normalized.monthlyPriceUsdCents,
    managedActionQuota: normalized.managedActionQuota,
    managedImageQuota: normalized.managedImageQuota,
    creatorPublishSlots: normalized.creatorPublishSlots,
    active: normalized.active,
    hostedProxyAccess: normalized.hostedProxyAccess,
    managedImages: normalized.managedImages,
    creatorConsoleAccess: normalized.creatorConsoleAccess,
    preforgedRealityAccess: normalized.preforgedRealityAccess,
  }
}

export function buildAdminCommerceFormMeta(): FormMetaData {
  return {
    id: {
      label: 'Plan',
      componentType: 'select',
      options: PLAN_OPTIONS,
      attributes: {
        class: 'admin-flag-select',
      },
    },
    label: {
      label: 'Public Label',
      componentType: 'input',
      attributes: {
        type: 'text',
        class: 'admin-flag-input',
        placeholder: 'Standard',
      },
    },
    monthlyPriceUsdCents: {
      label: 'Monthly Price (cents)',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    managedActionQuota: {
      label: 'Managed Actions / Month',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    managedImageQuota: {
      label: 'Managed Images / Month',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    creatorPublishSlots: {
      label: 'Creator Publish Slots',
      componentType: 'input',
      attributes: {
        type: 'number',
        min: '0',
        step: '1',
        class: 'admin-flag-input',
      },
    },
    active: {
      label: 'Plan Active',
      componentType: 'input',
      attributes: {
        type: 'checkbox',
        class: 'admin-flag-checkbox',
      },
    },
    hostedProxyAccess: {
      label: 'Hosted Proxy Access',
      componentType: 'input',
      attributes: {
        type: 'checkbox',
        class: 'admin-flag-checkbox',
      },
    },
    managedImages: {
      label: 'Managed Images',
      componentType: 'input',
      attributes: {
        type: 'checkbox',
        class: 'admin-flag-checkbox',
      },
    },
    creatorConsoleAccess: {
      label: 'Creator Console Access',
      componentType: 'input',
      attributes: {
        type: 'checkbox',
        class: 'admin-flag-checkbox',
      },
    },
    preforgedRealityAccess: {
      label: 'Preforged Realities Included',
      componentType: 'input',
      attributes: {
        type: 'checkbox',
        class: 'admin-flag-checkbox',
      },
    },
  }
}

export function buildAdminCommerceSubmission(values: Record<string, unknown>): CommercePlan {
  return normalizeCommercePlan({
    id: values.id,
    label: values.label,
    monthlyPriceUsdCents: values.monthlyPriceUsdCents,
    managedActionQuota: values.managedActionQuota,
    managedImageQuota: values.managedImageQuota,
    creatorPublishSlots: values.creatorPublishSlots,
    active: values.active,
    hostedProxyAccess: values.hostedProxyAccess,
    managedImages: values.managedImages,
    creatorConsoleAccess: values.creatorConsoleAccess,
    preforgedRealityAccess: values.preforgedRealityAccess,
  })
}
